import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

interface Question {
  id: string
  text: string
  type: 'BOOLEAN' | 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'RATING' | 'COUNTRY'
  required: boolean
  order: number
  options: string[]
  scoring_config?: {
    type: string
    min_value?: number
    max_value?: number
    true_value?: number
    false_value?: number
    option_weights?: Record<string, number>
  }
}

interface ScoreBreakdownProps {
  score: number
  scoreBreakdown: Record<string, number>
  questions: Question[]
  responses: Record<string, any>
}

function getMaxScore(q: Question): number | null {
  const cfg = q.scoring_config
  if (!cfg) return null
  switch (cfg.type) {
    case 'direct_scale':
      return cfg.max_value ?? 5
    case 'binary':
      return Math.max(cfg.true_value ?? 1, cfg.false_value ?? 0)
    case 'weighted_options':
      if (!cfg.option_weights) return null
      return Math.max(...Object.values(cfg.option_weights))
    case 'multi_select_sum':
      if (!cfg.option_weights) return null
      return Object.values(cfg.option_weights).reduce((a, b) => a + b, 0)
    case 'qualitative':
      return null
    default:
      return null
  }
}

function formatAnswer(value: any, type: string, intl: IntlShape): string {
  if (value === null || value === undefined || value === '') return intl.formatMessage({ id: 'score.no_answer' })
  if (type === 'BOOLEAN') return value === true ? intl.formatMessage({ id: 'score.yes' }) : intl.formatMessage({ id: 'score.no' })
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object' && value !== null) {
    if (value.country) {
      return value.region ? `${value.country}, ${value.region}` : value.country
    }
    return JSON.stringify(value)
  }
  return String(value)
}

function getScoreColor(pct: number): string {
  if (pct >= 80) return 'text-success'
  if (pct >= 60) return 'text-primary'
  if (pct >= 40) return 'text-warning'
  return 'text-error'
}

function getBarColor(pct: number): string {
  if (pct >= 80) return 'bg-success'
  if (pct >= 60) return 'bg-primary'
  if (pct >= 40) return 'bg-warning'
  return 'bg-error'
}

const ScoreBreakdown: React.FC<ScoreBreakdownProps> = ({ score, scoreBreakdown, questions, responses }) => {
  const intl = useIntl()
  const scoredQuestions = questions.filter(q => q.scoring_config && q.scoring_config.type !== 'qualitative')
  const maxScore = scoredQuestions.reduce((sum, q) => sum + (getMaxScore(q) || 0), 0)
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0

  // Find strongest and weakest
  let _strongest: { text: string; pct: number } | null = null
  let _weakest: { text: string; pct: number } | null = null

  scoredQuestions.forEach(q => {
    const qScore = scoreBreakdown[q.id] ?? 0
    const qMax = getMaxScore(q)
    if (!qMax) return
    const qPct = Math.round((qScore / qMax) * 100)
    if (!_strongest || qPct > _strongest.pct) _strongest = { text: q.text, pct: qPct }
    if (!_weakest || qPct < _weakest.pct) _weakest = { text: q.text, pct: qPct }
  })

  const strongest = _strongest as { text: string; pct: number } | null
  const weakest = _weakest as { text: string; pct: number } | null

  return (
    <div className="space-y-8">
      {/* Overall Score */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-32 h-32 rounded-full border-4 border-primary-light bg-primary/10 mb-4">
          <div>
            {/* Points lead, percentage supports (request 1). */}
            <div className={`text-4xl font-bold ${getScoreColor(percentage)}`} data-testid="overall-points">
              {score.toLocaleString('en-US')}
            </div>
            <div className="text-xs text-foreground-muted mt-1">
              <FormattedMessage id="score.of_pts" values={{ max: maxScore.toLocaleString('en-US'), pct: percentage }} />
            </div>
          </div>
        </div>
        <h3 className="text-xl font-semibold text-foreground">
          {percentage >= 80 ? (
            <FormattedMessage id="score.rating_excellent" />
          ) : percentage >= 60 ? (
            <FormattedMessage id="score.rating_good" />
          ) : percentage >= 40 ? (
            <FormattedMessage id="score.rating_room" />
          ) : (
            <FormattedMessage id="score.rating_keep_going" />
          )}
        </h3>
      </div>

      {/* Insights */}
      {strongest && weakest && strongest.text !== weakest.text && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-success-light rounded-lg p-4 border border-success">
            <p className="text-xs font-semibold text-success uppercase tracking-wide mb-1"><FormattedMessage id="score.strongest_area" /></p>
            <p className="text-sm text-foreground font-medium">{strongest.text}</p>
            <p className="text-lg font-bold text-success mt-1">{strongest.pct}%</p>
          </div>
          <div className="bg-warning-light rounded-lg p-4 border border-warning">
            <p className="text-xs font-semibold text-warning uppercase tracking-wide mb-1"><FormattedMessage id="score.area_for_growth" /></p>
            <p className="text-sm text-foreground font-medium">{weakest.text}</p>
            <p className="text-lg font-bold text-warning mt-1">{weakest.pct}%</p>
          </div>
        </div>
      )}

      {/* Per-Question Breakdown */}
      <div>
        <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4"><FormattedMessage id="score.question_breakdown" /></h4>
        <div className="space-y-4">
          {questions.map((q, i) => {
            const qScore = scoreBreakdown[q.id]
            const qMax = getMaxScore(q)
            const isQualitative = q.scoring_config?.type === 'qualitative' || !q.scoring_config
            const qPct = qMax && qMax > 0 ? Math.round(((qScore ?? 0) / qMax) * 100) : 0

            return (
              <div key={q.id} className="bg-surface border border-border-light rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 mr-4">
                    <p className="text-sm font-medium text-foreground">
                      {i + 1}. {q.text}
                    </p>
                    <p className="text-xs text-foreground-muted mt-1">
                      <FormattedMessage id="score.your_answer" />{' '}
                      <span className="font-medium text-foreground">{formatAnswer(responses[q.id], q.type, intl)}</span>
                    </p>
                  </div>
                  {isQualitative ? (
                    <span className="text-xs text-foreground-muted bg-background-alt px-2 py-1 rounded whitespace-nowrap"><FormattedMessage id="score.no_score" /></span>
                  ) : (
                    <span className={`text-sm font-bold whitespace-nowrap ${getScoreColor(qPct)}`}>
                      {qScore ?? 0} / {qMax}
                    </span>
                  )}
                </div>
                {!isQualitative && qMax && (
                  <div className="w-full bg-background-alt rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${getBarColor(qPct)}`}
                      style={{ width: `${qPct}%` }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ScoreBreakdown
